import { useState, useMemo, useEffect } from "react";
import type { Project, ActionItem, EditingActionItem } from "./types";

interface ActionableItemsViewProps {
  projects: Project[];
  globalActionItems: ActionItem[];
  onUpdateProject: (project: Project) => void;
  onUpdateGlobalActionItems: (items: ActionItem[]) => void;
  className?: string;
}

interface ActionableItemWithContext extends ActionItem {
  projectId: number; // -1 for global items
  projectName?: string;
  actionIndex: number;
}

export default function ActionableItemsView({
  projects,
  globalActionItems,
  onUpdateProject,
  onUpdateGlobalActionItems,
  className = "",
}: ActionableItemsViewProps) {
  const [editingActionItem, setEditingActionItem] =
    useState<EditingActionItem | null>(null);

  // Flatten all actionable items from all projects and global items
  const actionableItems = useMemo((): ActionableItemWithContext[] => {
    const items: ActionableItemWithContext[] = [];

    // Add actionable items from projects
    projects.forEach((project) => {
      project.actionItems.forEach((actionItem, actionIndex) => {
        if (actionItem.status === "actionable") {
          items.push({
            ...actionItem,
            projectId: project.id,
            projectName: project.name,
            actionIndex,
          });
        }
      });
    });

    // Add actionable global items
    globalActionItems.forEach((actionItem, actionIndex) => {
      if (actionItem.status === "actionable") {
        items.push({
          ...actionItem,
          projectId: -1,
          actionIndex,
        });
      }
    });

    return items;
  }, [projects, globalActionItems]);

  const handleEditActionItem = (
    projectId: number,
    actionIndex: number,
    fieldIndex: number,
  ) => {
    const item = actionableItems.find(
      (item) =>
        item.projectId === projectId && item.actionIndex === actionIndex,
    );

    if (item) {
      setEditingActionItem({
        id: item.id,
        content: item.content,
        date: item.date || "",
        status: item.status,
        delegatedTo: item.delegatedTo || "",
        fieldIndex,
        projectId,
        actionIndex,
      });
    }
  };

  const handleActionItemFieldChange = (
    field: keyof EditingActionItem,
    value: string | number,
  ) => {
    setEditingActionItem((prev) => (prev ? { ...prev, [field]: value } : null));
  };

  const handleActionItemSave = () => {
    if (!editingActionItem) return;

    const updatedActionItem = {
      id: editingActionItem.id === -1 ? Date.now() : editingActionItem.id,
      content: editingActionItem.content,
      date: editingActionItem.date || undefined,
      status: editingActionItem.status,
      delegatedTo: editingActionItem.delegatedTo || undefined,
    };

    if (editingActionItem.projectId === -1) {
      if (editingActionItem.actionIndex === -1) {
        // Add new global action item
        const updatedGlobalItems = [...globalActionItems, updatedActionItem];
        onUpdateGlobalActionItems(updatedGlobalItems);
      } else {
        // Update existing global action item
        const updatedGlobalItems = globalActionItems.map((item, index) =>
          index === editingActionItem.actionIndex ? updatedActionItem : item,
        );
        onUpdateGlobalActionItems(updatedGlobalItems);
      }
    } else {
      // Update project action item
      const project = projects.find(
        (p) => p.id === editingActionItem.projectId,
      );
      if (project) {
        const updatedProject = {
          ...project,
          actionItems: project.actionItems.map((item, index) =>
            index === editingActionItem.actionIndex ? updatedActionItem : item,
          ),
        };
        onUpdateProject(updatedProject);
      }
    }

    setEditingActionItem(null);
  };

  const handleActionItemCancel = () => {
    setEditingActionItem(null);
  };

  const handleAddNew = (projectId: number) => {
    // Only allow adding global action items (projectId === -1)
    if (projectId === -1) {
      setEditingActionItem({
        id: -1,
        content: "",
        date: "",
        status: "actionable",
        delegatedTo: "",
        fieldIndex: 0,
        projectId: -1,
        actionIndex: -1,
      });
    }
  };

  // Auto-focus the first actionable item when component mounts
  useEffect(() => {
    if (actionableItems.length > 0) {
      // Try to find the first actionable item container
      const firstItem = document.querySelector(
        "[data-actionable-item]",
      ) as HTMLElement;
      if (firstItem) {
        firstItem.focus();
      }
    } else {
      // If no actionable items, focus on the "Add New" area
      const addNewElement = document.querySelector(
        '[tabindex="0"]',
      ) as HTMLElement;
      if (addNewElement) {
        addNewElement.focus();
      }
    }
  }, [actionableItems.length]);

  return (
    <div className={`max-w-4xl mx-auto p-6 ${className}`}>
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">Actionable Items</h1>
        <div className="text-sm text-gray-600 bg-gray-50 p-3 rounded">
          <strong>Filter View:</strong> Showing only actionable items from all
          projects ({actionableItems.length} items)
        </div>
      </div>

      {actionableItems.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <div className="text-lg mb-2">🎉 All caught up!</div>
          <p>No actionable items found across all projects.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {actionableItems.map((item) => (
            <div
              key={`${item.projectId}-${item.actionIndex}-${item.id}`}
              className="bg-white border border-gray-200 rounded-lg p-4"
            >
              {item.projectName && (
                <div className="text-xs text-gray-500 mb-2">
                  📁 {item.projectName}
                </div>
              )}
              <div
                className={`p-3 rounded focus:outline-none ${
                  editingActionItem?.projectId === item.projectId &&
                  editingActionItem?.actionIndex === item.actionIndex
                    ? "ring-2 ring-green-300 bg-green-50"
                    : "hover:border-gray-300"
                }`}
                data-actionable-item
                tabIndex={0}
              >
                {editingActionItem?.projectId === item.projectId &&
                editingActionItem?.actionIndex === item.actionIndex ? (
                  <div className="space-y-2">
                    <input
                      type="text"
                      placeholder="Action item content"
                      value={editingActionItem.content}
                      onChange={(e) =>
                        handleActionItemFieldChange("content", e.target.value)
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleActionItemSave();
                        } else if (e.key === "Escape") {
                          e.preventDefault();
                          handleActionItemCancel();
                        }
                      }}
                      className="w-full p-2 border border-green-500 rounded"
                      autoFocus
                    />
                    <div className="flex gap-2">
                      <select
                        value={editingActionItem.status}
                        onChange={(e) =>
                          handleActionItemFieldChange("status", e.target.value)
                        }
                        className="p-2 border border-green-500 rounded"
                      >
                        <option value="actionable">Actionable</option>
                        <option value="done">Done</option>
                        <option value="not actionable">Not Actionable</option>
                      </select>
                      <input
                        type="date"
                        value={editingActionItem.date}
                        onChange={(e) =>
                          handleActionItemFieldChange("date", e.target.value)
                        }
                        className="p-2 border border-green-500 rounded"
                      />
                      <input
                        type="text"
                        placeholder="Delegated to"
                        value={editingActionItem.delegatedTo}
                        onChange={(e) =>
                          handleActionItemFieldChange(
                            "delegatedTo",
                            e.target.value,
                          )
                        }
                        className="flex-1 p-2 border border-green-500 rounded"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span
                          className="cursor-pointer hover:text-green-600 transition-colors"
                          onClick={() =>
                            handleEditActionItem(
                              item.projectId,
                              item.actionIndex,
                              0,
                            )
                          }
                        >
                          {item.content}
                        </span>
                        <span
                          className="px-2 py-1 rounded text-xs cursor-pointer hover:opacity-75 transition-opacity bg-green-100 text-green-800"
                          onClick={() =>
                            handleEditActionItem(
                              item.projectId,
                              item.actionIndex,
                              1,
                            )
                          }
                        >
                          Actionable
                        </span>
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {item.date && (
                          <span
                            className="cursor-pointer hover:text-green-600 transition-colors"
                            onClick={() =>
                              handleEditActionItem(
                                item.projectId,
                                item.actionIndex,
                                2,
                              )
                            }
                          >
                            Due: {item.date}
                          </span>
                        )}
                        {item.delegatedTo && (
                          <span
                            className="ml-2 cursor-pointer hover:text-green-600 transition-colors"
                            onClick={() =>
                              handleEditActionItem(
                                item.projectId,
                                item.actionIndex,
                                3,
                              )
                            }
                          >
                            Delegated to: {item.delegatedTo}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add New Global Action Item */}
      <div className="mt-8 border-t pt-6">
        <h3 className="text-lg font-semibold mb-4">
          Add New Global Action Item
        </h3>
        <div
          className={`p-3 rounded border-2 border-dashed cursor-pointer focus:outline-none ${
            editingActionItem?.projectId === -1 &&
            editingActionItem?.actionIndex === -1
              ? "ring-2 ring-green-300 bg-green-50 border-green-300"
              : "border-gray-300 hover:border-green-300 bg-gray-50"
          }`}
          onClick={() => handleAddNew(-1)}
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleAddNew(-1);
            }
          }}
        >
          {editingActionItem?.projectId === -1 &&
          editingActionItem?.actionIndex === -1 ? (
            <div className="space-y-2" onClick={(e) => e.stopPropagation()}>
              <input
                type="text"
                placeholder="New global action item content"
                value={editingActionItem?.content || ""}
                onChange={(e) =>
                  handleActionItemFieldChange("content", e.target.value)
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleActionItemSave();
                  } else if (e.key === "Escape") {
                    e.preventDefault();
                    handleActionItemCancel();
                  }
                }}
                className="w-full p-2 border border-green-500 rounded"
                autoFocus
              />
              <div className="flex gap-2">
                <select
                  value={editingActionItem?.status || "actionable"}
                  onChange={(e) =>
                    handleActionItemFieldChange("status", e.target.value)
                  }
                  className="p-2 border border-green-500 rounded"
                >
                  <option value="actionable">Actionable</option>
                  <option value="done">Done</option>
                  <option value="not actionable">Not Actionable</option>
                </select>
                <input
                  type="date"
                  value={editingActionItem?.date || ""}
                  onChange={(e) =>
                    handleActionItemFieldChange("date", e.target.value)
                  }
                  className="p-2 border border-green-500 rounded"
                />
                <input
                  type="text"
                  placeholder="Delegated to"
                  value={editingActionItem?.delegatedTo || ""}
                  onChange={(e) =>
                    handleActionItemFieldChange("delegatedTo", e.target.value)
                  }
                  className="flex-1 p-2 border border-green-500 rounded"
                />
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center text-gray-500">
              <span className="text-lg">+ Add New Global Action Item</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
